import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { MapPin, Building2, ShieldCheck, Clock3 } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/ui-kit/Reveal";

const STATS = [
  { icon: MapPin, value: 45, suffix: "+", label: "No. of Locations" },
  { icon: Building2, value: 30, suffix: "+", label: "Trusted Clients" },
  { icon: ShieldCheck, value: 250, suffix: "+", label: "Trained Personnel" },
  { icon: Clock3, value: 24, suffix: "/7", label: "Support & Response" },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(to);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, to, { duration: 2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{n.toLocaleString("en-IN")}<span className="text-[var(--red)]">{suffix}</span></span>;
};

export default function TrustBanner() {
  return (
    <section data-testid="trust-stats-banner" className="relative bg-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-6">
            <Eyebrow>Why choose us</Eyebrow>
            <h2 className="mt-5 font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9] text-[var(--ink)]">
              Trusted by those <span className="text-[var(--navy)]">who can't</span> afford risk.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.1}>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              To deliver unparalleled security and facility management services that ensure complete peace of mind. We achieve this through disciplined personnel, rapid response capabilities, and a relentless commitment to our clients' safety, privacy, and operational efficiency.
            </p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t-2 border-[var(--ink)]">
          {STATS.map(({ icon: Icon, value, suffix, label }, i) => (
            <Reveal key={label} delay={i * 0.08} className={`group py-8 sm:py-10 pr-4 ${i % 2 ? "pl-4 sm:pl-8" : ""} ${i > 0 ? "lg:pl-8" : ""} border-b lg:border-b-0 ${i % 2 === 0 ? "border-r" : ""} lg:border-r ${i === 3 ? "lg:border-r-0" : ""} border-slate-200`}>
              <div data-testid={`trust-stat-${i}`}>
                <Icon className="w-7 h-7 text-[var(--navy)] transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-8deg]" />
                <div className="mt-6 font-display italic font-black text-5xl sm:text-7xl leading-none text-[var(--ink)]">
                  <Counter to={value} suffix={suffix} />
                </div>
                <div className="mt-3 text-sm font-semibold uppercase tracking-wider text-slate-600">{label}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <p data-testid="trust-stats-caption" className="mt-6 text-xs text-slate-500">Our numbers over the years.</p>
      </div>
    </section>
  );
}
